import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import axios from 'axios';

interface User {
  id: string;
  email: string;
  name: string;
  role: 'participant' | 'organizer' | 'admin';
  emailVerified?: boolean;
}

interface AuthContextType {
  user: User | null;
  accessToken: string | null;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, name: string, recaptchaToken: string, dietaryRestrictions?: string) => Promise<void>;
  socialLogin: (provider: string, idToken: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [lastActivity, setLastActivity] = useState<number>(Date.now());

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

  // Logout function (needs to be defined before axios interceptor)
  const logout = useCallback(() => {
    setUser(null);
    setAccessToken(null);
    localStorage.removeItem('user');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('tokenExpiry');
  }, []);

  // Validate token expiry
  const isTokenValid = useCallback(() => {
    const tokenExpiry = localStorage.getItem('tokenExpiry');
    if (!tokenExpiry) return false;
    
    const expiryTime = parseInt(tokenExpiry, 10);
    const now = Date.now();
    
    // Token is valid if expiry is more than 1 minute in the future
    return expiryTime > now + 60000;
  }, []);

  // Setup axios interceptor for handling auth errors
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        // Don't logout for logs endpoint errors - let component handle it
        const isLogsEndpoint = error.config?.url?.includes('/logs');
        
        // If we get 401 or 403, automatically logout (except for logs endpoint)
        if ((error.response?.status === 401 || error.response?.status === 403) && !isLogsEndpoint) {
          console.log('Authentication error detected, logging out...');
          logout();
          // Redirect to login if not already there
          if (!window.location.pathname.includes('/logg-inn')) {
            window.location.href = '/logg-inn';
          }
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, [logout]);

  // Track user activity and handle inactivity timeout
  useEffect(() => {
    if (!user) return;

    const updateActivity = () => {
      setLastActivity(Date.now());
    };

    // Track various user activities
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach(event => {
      window.addEventListener(event, updateActivity);
    });

    // Check for inactivity every minute
    const inactivityInterval = setInterval(() => {
      const timeSinceLastActivity = Date.now() - lastActivity;
      
      if (timeSinceLastActivity > INACTIVITY_TIMEOUT) {
        console.log('User inactive for too long, logging out...');
        logout();
        alert('Du har blitt logget ut på grunn av inaktivitet.');
      }
    }, 60000); // Check every minute

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, updateActivity);
      });
      clearInterval(inactivityInterval);
    };
  }, [user, lastActivity, logout]);

  useEffect(() => {
    // Load from localStorage on mount and validate token
    const storedToken = localStorage.getItem('accessToken');
    const storedUser = localStorage.getItem('user');

    if (storedToken && storedUser) {
      // Check if token is still valid
      if (isTokenValid()) {
        setAccessToken(storedToken);
        setUser(JSON.parse(storedUser));
      } else {
        // Token expired, clear everything
        console.log('Stored token has expired, clearing auth state');
        logout();
      }
    }
    setIsLoading(false);
  }, [isTokenValid, logout]);

  const login = async (email: string, password: string) => {
    const response = await axios.post(`${API_URL}/auth/login`, { email, password });
    const { user, accessToken, refreshToken } = response.data;

    // Calculate token expiry (15 minutes from now, matching JWT_ACCESS_EXPIRY)
    const tokenExpiry = Date.now() + 15 * 60 * 1000;

    setUser(user);
    setAccessToken(accessToken);
    setLastActivity(Date.now());
    localStorage.setItem('user', JSON.stringify(user));
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
    localStorage.setItem('tokenExpiry', tokenExpiry.toString());
  };

  const register = async (email: string, password: string, name: string, recaptchaToken: string, dietaryRestrictions?: string) => {
    const response = await axios.post(`${API_URL}/auth/register`, { 
      email, 
      password, 
      name, 
      recaptchaToken,
      dietaryRestrictions 
    });
    const { user, accessToken, refreshToken } = response.data;

    // Calculate token expiry (15 minutes from now)
    const tokenExpiry = Date.now() + 15 * 60 * 1000;

    setUser(user);
    setAccessToken(accessToken);
    setLastActivity(Date.now());
    localStorage.setItem('user', JSON.stringify(user));
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
    localStorage.setItem('tokenExpiry', tokenExpiry.toString());
  };

  const socialLogin = async (provider: string, idToken: string) => {
    const response = await axios.post(`${API_URL}/auth/social/login`, { provider, idToken });
    const { user, accessToken, refreshToken } = response.data;

    // Calculate token expiry (15 minutes from now)
    const tokenExpiry = Date.now() + 15 * 60 * 1000;

    setUser(user);
    setAccessToken(accessToken);
    setLastActivity(Date.now());
    localStorage.setItem('user', JSON.stringify(user));
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
    localStorage.setItem('tokenExpiry', tokenExpiry.toString());
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        accessToken,
        login,
        register,
        socialLogin,
        logout,
        isAuthenticated: !!user,
        isLoading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
